import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { isEmpty } from 'lodash'
import { Box, Typography } from '@material-ui/core'

import { MyDocumentsTable } from './components'
import { ConfirmationDialog, LoadingFeedback } from 'components'

import helpers from 'helpers'

import useFetch from 'hooks/useFetch'
import useSearch from 'hooks/useSearch'
import useSnackbar from 'hooks/useSnackbar'
import usePagination from 'hooks/usePagination'

import theme from 'theme'
import useStyles from './styles'
import * as service from 'service'
import constants from 'constants/index'

const MyDocuments = ({
  filter,
  setLoading,
  setHasDocument,
  defaultFilters = {},
}) => {
  const { INACTIVE_STATUS_ID, ACTIVE_STATUS_ID } = constants.documents

  const { perPage, page, handleChangePage, handleChangeRowsPerPage } =
    usePagination(12)

  const [openInactiveDialog, setOpenInactiveDialog] = useState(false)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [wasResetFilters, setWasResetFilters] = useState(false)
  const [isLoadingDocumentCopy, setIsLoadingDocumentCopy] = useState(false)
  const [documentData, setDocumentData] = useState()

  const snackbar = useSnackbar()
  const classes = useStyles()

  const { searchInput, handleSearchValue, isSearching } = useSearch((value) => {
    const newFilters = { ...filter.filters }
    if (value) {
      newFilters.titleOrCategory = value
    } else {
      delete newFilters.titleOrCategory
    }
    filter.setFilters(newFilters)
  })

  const { response, isLoading, refresh } = useFetch(
    service.dponet.privacyPolicies.get,
    {
      order: 'created_at DESC',
      ...filter.filters,
      ...defaultFilters,
      page,
      perPage,
    },
    [perPage, page, filter.filters, wasResetFilters],
    !!wasResetFilters,
  )

  const documents = response?.data?.privacyPolicies

  const isInactive = documentData?.status === INACTIVE_STATUS_ID

  const handleOpenInactiveDialog = (id, status) => {
    setDocumentData({ id, status })
    setOpenInactiveDialog(true)
  }

  const handleOpenDeleteDialog = (id, hasConsentForm = false) => {
    setDocumentData({ id, hasConsentForm })
    setOpenDeleteDialog(true)
  }

  const handleDownloadDocumentCopy = async (currentDocument) => {
    try {
      setIsLoadingDocumentCopy(true)

      const response = await service.dponet.privacyPolicies.exportDocument({
        privacyPolicyId: currentDocument?.id,
      })

      const fileUrl = response?.data?.fileUrl

      if (!fileUrl) {
        snackbar.open({
          message: 'Ocorreu um erro ao exportar o documento.',
          variant: 'error',
        })

        return
      }

      window.open(fileUrl, '_blank')
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error.response.data.error),
        variant: 'error',
      })
    } finally {
      setIsLoadingDocumentCopy(false)
    }
  }

  const handleCloseInactiveDialog = () => setOpenInactiveDialog(false)
  const handleCloseDeleteDialog = () => setOpenDeleteDialog(false)

  const resetFilters = async (filters = {}) => await filter.setFilters(filters)

  const handleDocumentInactivation = async () => {
    try {
      setLoading(true)

      const status = isInactive ? ACTIVE_STATUS_ID : INACTIVE_STATUS_ID

      await service.dponet.privacyPolicies.put({
        privacyPolicyId: documentData?.id,
        status,
      })

      snackbar.open({
        message: `Documento ${
          isInactive ? 'ativado' : 'inativado'
        } com sucesso!`,
        variant: 'success',
      })

      handleCloseInactiveDialog()
      refresh()
    } catch (error) {
      console.error(error)
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          `Ocorreu uma falha ao tentar ${
            isInactive ? 'ativar' : 'inativar'
          } o documento`,
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleDocumentDeletion = async () => {
    try {
      setLoading(true)

      await service.dponet.privacyPolicies.destroy({
        privacyPolicyId: documentData?.id,
      })

      snackbar.open({
        message: 'Documento excluído com sucesso!',
        variant: 'success',
      })

      handleCloseDeleteDialog()
      refresh()
    } catch (error) {
      console.error(error)
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Ocorreu uma falha ao tentar excluir o documento',
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const deleteDialogText = () => {
    if (documentData?.hasConsentForm) {
      return (
        <Typography variant="h3">
          Tem certeza de que deseja excluir este documento?&nbsp;
          <span className={classes.textError}>
            Após o aceites vinculado, todos os aceites serão excluidos, e sua
            exclusão não poderá ser desfeita!
          </span>
        </Typography>
      )
    }

    return (
      <Typography variant="h3">
        Tem certeza de que deseja excluir este documento?&nbsp;
        <span className={classes.textError}>
          Esta ação não poderá ser desfeita!
        </span>
      </Typography>
    )
  }

  // Os filtros que já vêm preenchidos são preservados: o useFilter os lê da query string, e é assim
  // que a tela consegue abrir já filtrada por um documento específico (ex.: o atalho "Ver na listagem
  // de documentos" na evidência do Projeto de Governança). Sobrescrevê-los aqui tornaria o filtro por
  // URL inútil nesta tela.
  useEffect(() => {
    resetFilters({ ...filter.filters, page, perPage })
    setWasResetFilters(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isEmpty(documents)) setHasDocument(true)
    else setHasDocument(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents])

  return (
    <Box
      display="flex"
      flexDirection="column"
      pt={3}
      gridGap={theme.spacing(3)}
    >
      <LoadingFeedback open={isLoadingDocumentCopy} />
      <Box display="flex" gridGap={theme.spacing(3)}>
        {constants.documents.STATUS_LABELS?.map((statusLabel, index) => (
          <Box
            key={index}
            display="flex"
            alignItems="center"
            gridGap={theme.spacing(1)}
          >
            <Box
              width={20}
              height={20}
              borderRadius={10}
              bgcolor={statusLabel.color}
            />
            <Typography variant="body2">{statusLabel?.label}</Typography>
          </Box>
        ))}
      </Box>
      <MyDocumentsTable
        documents={documents}
        isLoading={isLoading}
        searchInput={searchInput}
        isSearching={isSearching}
        handleSearchValue={handleSearchValue}
        handleOpenInactiveDialog={handleOpenInactiveDialog}
        handleOpenDeleteDialog={handleOpenDeleteDialog}
        handleDownloadDocumentCopy={handleDownloadDocumentCopy}
        count={response?.data?.meta?.totalCount || 0}
        pagination={{
          perPage,
          page,
          handleChangePage,
          handleChangeRowsPerPage,
        }}
      />
      <ConfirmationDialog
        open={openInactiveDialog}
        setOpen={setOpenInactiveDialog}
        text={`Tem certeza de que deseja ${
          isInactive ? 'ativar' : 'inativar'
        } este documento?`}
        actionAcceptButton={handleDocumentInactivation}
        textButton={isInactive ? 'Ativar' : 'Inativar'}
        textButtonCancel="Cancelar"
        hiddenIcon
        disableBackdropClick
      />
      <ConfirmationDialog
        open={openDeleteDialog}
        setOpen={setOpenDeleteDialog}
        text={deleteDialogText()}
        textButton="Excluir"
        actionAcceptButton={handleDocumentDeletion}
        hiddenIcon
        disableBackdropClick
        variant="error"
      />
    </Box>
  )
}

MyDocuments.propTypes = {
  filter: PropTypes.object.isRequired,
  setLoading: PropTypes.func.isRequired,
  setHasDocument: PropTypes.func.isRequired,
  defaultFilters: PropTypes.object,
}

export default MyDocuments
